import { useMutation, useQuery } from "@tanstack/react-query";
import { useAuth } from "../../../../../auth/AuthContext";
import { indriveCheckoutService } from "../services/indrive-checkout-service";
import { IndriveCheckoutItem } from "../types";

export const indriveCheckoutKeys = {
  all: ["indrive-checkout"] as const,
  list: (dbName: string) =>
    [...indriveCheckoutKeys.all, "list", dbName] as const,
};

export function useIndriveCheckout(dbName: string) {
  const { user } = useAuth();

  const {
    data: checkoutList = [],
    isPending,
    refetch,
  } = useQuery({
    queryKey: indriveCheckoutKeys.list(dbName),
    queryFn: async (): Promise<IndriveCheckoutItem[]> => {
      const response = await indriveCheckoutService.getCheckoutList({
        dbName,
        ops: "indrive",
      });
      return response.data || [];
    },
    enabled: !!dbName,
    staleTime: 1000 * 60 * 2,
    refetchInterval: 20000,
  });

  const confirmCheckoutMutation = useMutation({
    mutationFn: async ({
      filterDate,
      ops,
    }: {
      filterDate: string;
      ops: string;
    }) => {
      if (!dbName || !user?.email) {
        throw new Error("Necessário e-mail do gestor e DBName");
      }
      return indriveCheckoutService.confirmCheckout({
        dbName,
        filterDate,
        userInfo: { email: user.email },
        ops,
      });
    },
    onSuccess: () => {
      setTimeout(() => {
        refetch();
      }, 3000);
    },
  });

  const startDocMutation = useMutation({
    mutationFn: async ({
      filterDate,
      ops,
    }: {
      filterDate: string;
      ops: string;
    }) => {
      if (!dbName || !user?.email) {
        throw new Error("Necessário e-mail do gestor e DBName");
      }

      return indriveCheckoutService.startDoc({
        dbName,
        filterDate,
        userInfo: { email: user.email },
        ops,
      });
    },
    onSuccess: () => {
      setTimeout(() => {
        refetch();
      }, 3000);
    },
  });

  const submitRemessaMutation = useMutation({
    mutationFn: async ({
      filterDate,
      ops,
    }: {
      filterDate: string;
      ops: string;
    }) => {
      if (!dbName || !user?.email) {
        throw new Error("Necessário e-mail do gestor e DBName");
      }

      return indriveCheckoutService.submitRemessa({
        dbName,
        filterDate,
        userInfo: { email: user.email },
        ops,
      });
    },
    onSuccess: () => {
      setTimeout(() => {
        refetch();
      }, 3000);
    },
  });

  const pushIndriveMutation = useMutation({
    mutationFn: async () => {
      if (!dbName) {
        throw new Error("Necessário selecionar um fundo");
      }

      return indriveCheckoutService.pushIndrive({
        dbName,
        ops: "indrive",
      });
    },
    onSuccess: () => {
      setTimeout(() => {
        refetch();
      }, 3000);
    },
  });

  return {
    checkoutList,
    isLoading: isPending,
    dbName,
    confirmCheckout: confirmCheckoutMutation.mutate,
    startDoc: startDocMutation.mutate,
    submitRemessa: submitRemessaMutation.mutate,
    pushIndrive: pushIndriveMutation.mutate,
    isConfirming: confirmCheckoutMutation.isPending,
    isStartingDoc: startDocMutation.isPending,
    isSubmittingRemessa: submitRemessaMutation.isPending,
    isPushingIndrive: pushIndriveMutation.isPending,
    pushSuccess: pushIndriveMutation.isSuccess,
    pushError: pushIndriveMutation.isError,
    refetch,
  };
}
